// Standalone Qualifying Event store at /qualifyingEvents/{eventId}. Lives
// independently of /tournaments — admins create a qualifying event BEFORE
// any tournament exists, players submit scores over days/weeks via the
// /qualify?q={eventId} self-register QR, then the admin approves the
// list and uses it as the basis to create the actual tournament (AI or
// manual). Nothing here writes to /tournaments.
//
// Tree shape:
//   /qualifyingEvents/{eventId}/
//     info:  { name, format, createdAt, status: 'open'|'closed', closedAt? }
//     teams/{teamId}/
//       teamName, player1, player2, createdAt,
//       score?, holes?, submittedAt?, approved?

import { onValue, push, ref, remove, set, update } from 'firebase/database'

import { db, tref, updateTenant } from './firebase.js'
import { generateTeamToken } from './teamTokens.js'
import { qeAudit } from './qeAudit.js'

export const QE_PATH = 'qualifyingEvents'

// Create a new event. Returns the eventId so the caller can navigate.
export async function createQualifyingEvent({ name, format, date = null, endDate = null, sourceRegistrationId = null, isPractice = false }) {
  const trimmedName = String(name || '').trim()
  if (!trimmedName) throw new Error('Event name is required.')
  const fmt = String(format || '').trim() || '4-Ball Best Ball'
  const node = push(tref(QE_PATH))
  const eventId = node.key
  // Inherited fields (date/endDate/source link) are optional and only written
  // when present, so existing callers that pass just {name, format} are unchanged.
  const info = {
    name: trimmedName,
    format: fmt,
    createdAt: Date.now(),
    status: 'open',
  }
  if (date) info.date = date
  if (endDate) info.endDate = endDate
  if (sourceRegistrationId) info.sourceRegistrationId = sourceRegistrationId
  if (isPractice) info.isPractice = true
  await set(node, { info })
  return eventId
}

export function watchQualifyingEvents(cb) {
  const r = tref(QE_PATH)
  return onValue(r, (snap) => cb(snap.val() || {}), (e) => console.warn('[db] listener error', e))
}

export function watchQualifyingEvent(eventId, cb) {
  const r = tref(`${QE_PATH}/${eventId}`)
  return onValue(r, (snap) => cb(snap.val()), (e) => console.warn('[db] listener error', e))
}

// Self-register check-in. Mirrors createQualifyingTeam in data.js but
// writes to /qualifyingEvents/{eventId}/teams/{teamId} so the new tree
// stays isolated from /tournaments.
export async function createQualifyingEventTeam(eventId, { teamName, player1, player2 }) {
  if (!eventId) throw new Error('Missing eventId.')
  const teamsRef = tref(`${QE_PATH}/${eventId}/teams`)
  const node = push(teamsRef)
  const teamId = node.key
  await set(node, {
    teamName: String(teamName || '').trim() || 'Team',
    player1: String(player1 || '').trim(),
    player2: String(player2 || '').trim(),
    createdAt: Date.now(),
    approved: false,
  })
  return teamId
}

// Seed a qualifying event's field from a registration list. Each registered
// team becomes a qualifying team so they show up in the event and qualify
// exactly like a self-checked-in team would — this only pre-loads the field,
// it does not change the qualifying flow. If a registration already carries a
// qualifying score (e.g. an imported / demo list), it comes across as a
// submitted score so the event shows those numbers instead of blanks; teams
// with no score come in unsubmitted and post their score like normal. `regs`
// is the /tournaments/{id}/registrations map. Returns the number seeded.
export async function seedQualifyingTeamsFromRegistrations(eventId, regs, opts = {}) {
  if (!eventId) throw new Error('Missing eventId.')
  const { autoScores = false } = opts
  const rows = Object.values(regs || {})
  if (rows.length === 0) return 0
  const base = `${QE_PATH}/${eventId}/teams`
  const now = Date.now()
  const updates = {}
  rows.forEach((r, i) => {
    const players = Array.isArray(r?.players) ? r.players : [r?.player1, r?.player2]
    const node = push(tref(base))
    const team = {
      teamName: String(r?.teamName || '').trim() || 'Team',
      player1: String(players[0] || '').trim(),
      player2: String(players[1] || '').trim(),
      createdAt: now,
      approved: false,
    }
    // Carry the CSV "Member" flag through qualifying into the build.
    if (r?.member != null) team.member = !!r.member
    let score = Number(r?.qualifyingScore)
    // Practice fields auto-fill a realistic qualifying spread so staff never
    // hand-enter 96 scores — the field arrives in Qualifying already scored.
    if (!(Number.isFinite(score) && score > 0) && autoScores) {
      score = 64 + ((i * 5 + (i % 7)) % 20) // ~64–83, varied, deterministic
    }
    // Carry the score across as a submitted result so the list displays it.
    if (Number.isFinite(score) && score > 0) {
      team.score = score
      team.submittedAt = now
    }
    updates[`${base}/${node.key}`] = team
  })
  await updateTenant(updates)
  return rows.length
}

// One-shot "send a registration to qualifying": create a linked qualifying
// event that INHERITS the registration's name/dates/format, tag it with the
// source, and seed its teams. Returns the new eventId. The CALLER writes
// qualifyingEventId back onto the registration (a /tournaments write, which
// this module deliberately never does). Used by both Registration's "Send to
// Qualifying" button and the New-Qualifying-Event picker so they behave identically.
export async function createQualifyingEventFromRegistration(registrationId, info = {}, regs = {}) {
  const eventId = await createQualifyingEvent({
    name: info.name,
    format: info.format,
    date: info.date || null,
    endDate: info.endDate || null,
    sourceRegistrationId: registrationId,
    isPractice: !!info.isPractice, // practice fields tag their qualifying event too (for reset)
  })
  if (regs && Object.keys(regs).length > 0) {
    // Practice fields auto-score on the way into Qualifying (no hand entry).
    await seedQualifyingTeamsFromRegistrations(eventId, regs, { autoScores: !!info.isPractice })
  }
  return eventId
}

// Patch the team with the final score + per-hole breakdown when the
// scorecard hits 18. `approved` left untouched — the admin flips it
// later from the detail page.
export async function submitQualifyingEventResult(eventId, teamId, { score, holes, teamName }) {
  if (!eventId || !teamId) throw new Error('Missing event or team id.')
  const patch = {
    score: Number(score) || 0,
    holes: holes || {},
    submittedAt: Date.now(),
  }
  if (teamName) patch.teamName = String(teamName).trim()
  await update(tref(`${QE_PATH}/${eventId}/teams/${teamId}`), patch)
}

// Approve the WHOLE field — every team in the event, digital score or not.
// Scores may all be on paper; approving locks the field with whoever's in, and
// digital submission is never required. (Reset Approvals is the way back.)
export async function approveAllTeams(eventId, teamsMap) {
  if (!eventId) return
  const updates = {}
  for (const teamId of Object.keys(teamsMap || {})) {
    updates[`${QE_PATH}/${eventId}/teams/${teamId}/approved`] = true
  }
  if (Object.keys(updates).length === 0) return
  await updateTenant(updates)
}

export async function setQualifyingEventStatus(eventId, status) {
  if (!eventId) return
  const patch = { status }
  if (status === 'closed') patch.closedAt = Date.now()
  if (status === 'open') patch.closedAt = null
  await update(tref(`${QE_PATH}/${eventId}/info`), patch)
}

// Accidental-delete lock (admin toggles it). Persists on the event's info.
export async function setQualifyingEventLocked(eventId, locked) {
  if (!eventId) return
  await update(tref(`${QE_PATH}/${eventId}/info`), { locked: !!locked })
}

export async function deleteQualifyingEvent(eventId) {
  if (!eventId) return
  qeAudit('deleteQualifyingEvent', { eventId }) // TEST tripwire: who deletes the event
  await remove(tref(`${QE_PATH}/${eventId}`))
}


// ── TEST: per-team QR tokens INSIDE the qualifying event ────────────────────
// A player scans their team's qualify QR (/play?e=<eventId>&tk=<token>) → the
// token IS the identity (no email/picker). Reverse index + token on each team:
//   teamTokens/{token} = { teamId, teamName, players }
//   teams/{teamId}/accessToken = token
// Assigns a token to every team that lacks one (idempotent; stable QRs).
export async function assignQualifyingEventTokens(eventId, teams) {
  if (!eventId) throw new Error('Missing eventId.')
  const updates = {}
  let created = 0
  for (const [teamId, team] of Object.entries(teams || {})) {
    if (team?.accessToken) continue
    const token = generateTeamToken()
    updates[`teams/${teamId}/accessToken`] = token
    updates[`teamTokens/${token}`] = {
      teamId,
      teamName: team?.teamName || teamId,
      players: [team?.player1 || '', team?.player2 || ''].filter(Boolean),
    }
    created++
  }
  if (created > 0) await update(tref(`${QE_PATH}/${eventId}`), updates)
  return created
}

// Token-bound qualifying submit → writes the score onto the event team. A
// hole-by-hole card is 'submitted'; an "enter final score only" card is
// 'pending' + manualEntry:true and stays unapproved for pro-shop review.
export async function submitTeamQualifyingEvent(eventId, teamId, payload) {
  const path = `${QE_PATH}/${eventId}/teams/${teamId}`
  await update(tref(path), {
    score: Number(payload.score) || 0,
    holes: payload.holes || null,
    byPlayer: payload.byPlayer || '',
    manualEntry: !!payload.manual,
    status: payload.manual ? 'pending' : 'submitted',
    approved: false,
    submittedAt: Date.now(),
  })
}
